import { request, SKIP_AUTH_HEADER } from './request'
import { useEnvApi } from '../composables/useEnvApi'
import { useServerFetch } from '../composables/useServerFetch'
import { useAppStore } from '../stores/app'
import { getBrandPageUrlBatch } from '../server/common'
import type {
  TradingAppPlatform,
  TradingAppRecommendation,
  TradingAppRecommendationResponse,
} from '../types/homeTradingApps'
import { WIKIBITWEBV1 } from '../utils/constants'
import { normalizeStoreBannerItems, type StoreBannerItem } from '../utils/homeStoreBanner'
import {
  createTradingAppTraderAliasMap,
  normalizeTradingAppRecommendations,
  type TradingAppTraderAliasMap,
} from '../utils/homeTradingApps'

export const STORE_RECOMMENDATIONS_UPSTREAM_PATH = '/api/software_ad/recommendation/list'
export const STORE_RECOMMENDATIONS_PROXY_PATH = '/api/store/recommendations'
export const STORE_RECOMMENDATIONS_AD_LIMIT = 100
export const STORE_BANNER_API_PATH = `/${WIKIBITWEBV1}/invoke-v3/v3`
export const STORE_BANNER_ROUTE = 'advertisement$apibanneradlist'

/** 获取指定平台的软件推荐位，SSR 直连上游，浏览器通过同源代理避免混合内容。 */
export const getTradingAppRecommendations = async (
  platform: TradingAppPlatform,
): Promise<TradingAppRecommendation[]> => {
  const api = useEnvApi()
  const params = {
    isWeb: true,
    platform,
    adLimit: STORE_RECOMMENDATIONS_AD_LIMIT,
  }
  const response = import.meta.server
    ? await useServerFetch<TradingAppRecommendationResponse>({
        baseURL: api.advertisement,
        url: STORE_RECOMMENDATIONS_UPSTREAM_PATH,
        method: 'GET',
        params,
      })
    : await request<unknown, TradingAppRecommendationResponse>({
        url: STORE_RECOMMENDATIONS_PROXY_PATH,
        method: 'GET',
        params,
        headers: {
          [SKIP_AUTH_HEADER]: '1',
        },
      })

  return normalizeTradingAppRecommendations(response)
}

/** 批量解析推荐应用关联交易所的品牌别名；接口异常时返回空映射，页面保留不可点击状态。 */
export const getTradingAppTraderAliases = async (
  traderCodes: readonly string[],
): Promise<TradingAppTraderAliasMap> => {
  const normalizedCodes = [...new Set(traderCodes.map((traderCode) => traderCode.trim()).filter(Boolean))]

  if (normalizedCodes.length === 0) return {}

  try {
    const response = await getBrandPageUrlBatch(normalizedCodes)
    return createTradingAppTraderAliasMap(response)
  } catch {
    return {}
  }
}

/** 获取首页 Banner 列表，SSR 直连数据中心，浏览器请求同一聚合接口。 */
export const getWikiStoreBanner = async (platform?: TradingAppPlatform): Promise<StoreBannerItem[]> => {
  const appStore = useAppStore()
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.testwikidatacenter : api.homeClient,
    url: STORE_BANNER_API_PATH,
    method: 'POST' as const,
    data: {
      isWeb: true,
      ...(platform ? { platform } : {}),
      countryCode: appStore.CountryCode,
    },
    headers: {
      Routes: STORE_BANNER_ROUTE,
    },
  }

  try {
    const response = import.meta.server ? await useServerFetch(requestConfig) : await request(requestConfig)

    return normalizeStoreBannerItems(response)
  } catch {
    // Banner 是首页增强内容，请求异常时由页面继续展示其它模块。
    return []
  }
}

/** 兼容其它首页模块沿用 Store Banner 命名的调用方。 */
export const getStoreBanner = getWikiStoreBanner
